import { useMemo } from "react";
import { Link } from "react-router";
import { ArrowUpRight } from "lucide-react";

import { LoadboardLabel } from "@/components/dataPages/loadboard";
import { CurrencyText, TranslatedText } from "@/components/common/text";
import { Hr } from "@/components/common/other/Hr";
import { Skeleton } from "@/components/common/other/Skeleton";
import { cn } from "@/utils/css";
import { generateLoadRatesUrl } from "@/utils/urls";
import { getLocationString } from "@/utils/text";
import { getMarketFromQuery } from "@/utils/zips";
import { useAreFeaturesUnlocked } from "@/auth/roles";

export const LoadboardCMRateInfo = ({ load, CMRate, trailerType }) => {
    const firstStop = load.itineraryStops?.[0];
    const lastStop = load.itineraryStops?.[load.itineraryStops.length - 1];
    const loadPickup = getMarketFromQuery(firstStop.zip, firstStop.stopCity, firstStop.stopState);
    const loadDelivery = getMarketFromQuery(lastStop.zip, lastStop.stopCity, lastStop.stopState);
    const areFeaturesUnlocked = useAreFeaturesUnlocked();

    // Only show CM rate info for non-multistop loads, and only for roles whose
    // features are unlocked (basic/locked users must not see the CM rate).
    const isMultistop = (load.itineraryStops?.length ?? 0) > 2;
    const showCMRateInfo = areFeaturesUnlocked && !isMultistop;

    // trailerType can be either an object with .id, a string ID directly, or undefined
    // Fall back to the load's trailer type if not provided
    const effectiveTrailerType = trailerType ?? load.trailerTypes?.[0];
    const trailerTypeObj = typeof effectiveTrailerType === "string" ? { id: effectiveTrailerType } : effectiveTrailerType;

    const urlParams = {
        trailerType: trailerTypeObj,
        pickup: loadPickup,
        pickupQuery: getLocationString(loadPickup, false),
        pickupDate: load.pickupDate,
        delivery: loadDelivery,
        deliveryQuery: getLocationString(loadDelivery, false),
        deliveryDate: null,
    };

    const loadSearchUrl = generateLoadRatesUrl(urlParams);

    const cmRatePercentage = useMemo(() => {
        if (CMRate.data?.suggestedRate && load.offerRate) {
            const num = (load.offerRate / CMRate.data.suggestedRate - 1) * 100;
            return Math.sign(num) * Math.round(Math.abs(num));
        }
        return 0;
    }, [CMRate.data, load.offerRate]);

    return (
        <>
            <LoadboardLabel text="Offered price" />
            <div className="flex flex-col mt-1 items-start gap-2 justify-between min-[1335px]:flex-row min-[1335px]:items-center">
                <div className="flex items-center flex-1">
                    <div className="w-[72px] text-left">
                        {load.offerRate ? <CurrencyText className="text-base/[25px]" num={load.offerRate} round /> : "-"}
                    </div>
                    <div className="bg-cm-p-outline h-5 w-[1.5px]" />
                    <div className="max-w-[67px] text-right flex-1">
                        {load.rpm ? <CurrencyText className="text-base/[25px] font-normal" num={load.rpm} /> : "-"}
                    </div>
                </div>
                <div className="flex w-[140px] justify-start min-[1335px]:justify-end items-center">
                    {showCMRateInfo && cmRatePercentage ? (
                        <div
                            className={cn("inline-flex gap-1 rounded-[20px] px-2 py-[2px] text-xs/[19px]", {
                                "bg-cm-p-container-green text-cm-p-on-container-green": cmRatePercentage > 0,
                                "bg-cm-p-container-red text-cm-p-on-container-red": cmRatePercentage < 0,
                            })}
                        >
                            <span>{Math.abs(cmRatePercentage)}%</span>
                            <TranslatedText>{cmRatePercentage > 0 ? "above" : "bellow"}</TranslatedText>
                            <TranslatedText>CM rate</TranslatedText>
                        </div>
                    ) : null}
                </div>
            </div>
            {showCMRateInfo && <Hr className="my-3 min-[1335px]:my-5" />}
            {showCMRateInfo ? (
                <>
                    <LoadboardLabel text="Recommended CM rate" />
                    <div className="flex flex-col items-start gap-2 justify-between min-[1335px]:flex-row min-[1335px]:items-center text-base/[25px] w-full">
                        {CMRate.isError ? (
                            <TranslatedText className="text-cm-p-text-secondary text-xs/[19px]" text="No info for this lane" />
                        ) : (
                            <div className="flex flex-row items-center flex-1">
                                <div className="w-[72px] text-left flex items-center justify-start">
                                    {CMRate.isLoading ? (
                                        <Skeleton className="w-12 h-4 rounded-lg" />
                                    ) : CMRate.data ? (
                                        <CurrencyText className="text-cm-p-accent text-base/[25px]" num={CMRate.data.suggestedRate} round />
                                    ) : (
                                        "-"
                                    )}
                                </div>
                                <div className="h-5 w-[1.5px] bg-cm-p-outline rounded" />
                                <div className="max-w-[67px] text-right flex items-center justify-end flex-1">
                                    {CMRate.isLoading ? (
                                        <Skeleton className="w-12 h-4 rounded-lg" />
                                    ) : CMRate.data ? (
                                        <CurrencyText
                                            className="text-cm-p-accent font-normal text-base/[25px]"
                                            num={CMRate.data.suggestedRpm}
                                        />
                                    ) : (
                                        "-"
                                    )}
                                </div>
                            </div>
                        )}
                        {CMRate.data ? (
                            <div className="flex-1 flex justify-end">
                                <Link
                                    to={loadSearchUrl}
                                    className="text-cm-p-accent text-xs/[19px] inline-flex gap-1 items-center font-medium"
                                >
                                    <TranslatedText>Load details</TranslatedText>
                                    <ArrowUpRight className="w-4" />
                                </Link>
                            </div>
                        ) : null}
                    </div>
                </>
            ) : null}
        </>
    );
};
